import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, FileText, Trash2, Printer, PenLine, Eraser } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { pdfStr, isRTL } from "@/lib/pdfStrings";
import { PdfLangMenu } from "@/components/PdfLangMenu";
import PhotoUploader from "@/components/PhotoUploader";

const EMPTY = { tipo: "recogida", envio_id: "", tracking_code: "", cliente_id: "", direccion: "", ciudad: "", pais: "", receptor_nombre: "", receptor_dni: "", receptor_relacion: "", firma_data: "", incidencia: "", bultos: 1, peso: 0, fecha: new Date().toISOString().slice(0, 10), notas: "", photo_ids: [], id_photo_id: "", merchandise_photo_id: "" };

export default function Albaranes() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/albaranes").then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => {
    load();
    api.get("/shipments").then(r => setShipments(r.data)).catch(()=>{});
    api.get("/clientes").then(r => setClients(r.data)).catch(()=>{});
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/albaranes", form);
      toast.success(t("saved"));
      setForm(EMPTY); setShowForm(false);
      load();
      printPDF(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/albaranes/${id}`);
    load();
  };

  const printPDF = async (a, langOverride = null) => {
    const activeLang = langOverride || "es";
    const S = pdfStr(activeLang);
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    // Preload Arabic font if AR selected OR any dynamic field contains Arabic
    const anyArabic = isRTL(activeLang) || [a.receptor_nombre, a.direccion, a.ciudad, a.incidencia, a.notas].some(hasArabic);
    if (anyArabic) await ensureArabicFont(doc);

    // ============ Fetch attached photos to embed at end of PDF ============
    // Collect all photo ids referenced by this receipt (photo_ids + legacy fields)
    const photoIds = Array.from(new Set([
      ...(a.photo_ids || []),
      ...(a.id_photo_id ? [a.id_photo_id] : []),
      ...(a.merchandise_photo_id ? [a.merchandise_photo_id] : []),
    ]));
    let photoBlobs = [];
    if (photoIds.length > 0) {
      try {
        const { data: files } = await api.get("/files", { params: { scope: "albaran" } });
        const byId = new Map((files || []).map(f => [f.id, f]));
        const token = localStorage.getItem("token") || "";
        for (const pid of photoIds) {
          const f = byId.get(pid);
          if (!f) continue;
          try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.storage_path}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!resp.ok) continue;
            const blob = await resp.blob();
            const dataUrl = await new Promise((resolve, reject) => {
              const rd = new FileReader();
              rd.onload = () => resolve(rd.result);
              rd.onerror = reject;
              rd.readAsDataURL(blob);
            });
            const fmt = (f.content_type || "").includes("png") ? "PNG" : "JPEG";
            photoBlobs.push({ dataUrl, fmt, name: f.original_filename || pid });
          } catch (e) { console.warn("Photo fetch failed:", e); }
        }
      } catch (e) { console.warn("Photos list failed:", e); }
    }
    let bc = null;
    const bcVal = (a.codigo || "").trim();
    if (bcVal) {
      try {
        const c = document.createElement("canvas");
        JsBarcode(c, bcVal, { format: "CODE128", displayValue: false, height: 42, width: 1.6, margin: 0 });
        bc = c.toDataURL("image/png");
      } catch(e){ console.warn("Albaran barcode failed:", e); }
    }
    const img = new Image();
    img.onload = () => { try { doc.addImage(img, "PNG", 40, 30, 70, 48); } catch(e){ console.warn("Albaran logo embed failed:", e); } render(); };
    img.onerror = render;
    img.src = "/logo.png";
    function render() {
      const title = a.tipo === "recogida" ? `${S.pickup.toUpperCase()} · ${S.receipt_title.toUpperCase()}` : `${S.delivery.toUpperCase()} · ${S.receipt_title.toUpperCase()}`;
      doc.setFont("helvetica","bold"); doc.setFontSize(16); doc.setTextColor(185,28,28);
      doc.text("MEOS CARGO", W - 40, 55, { align: "right" });
      doc.setFontSize(9); doc.setFont("helvetica","normal"); doc.setTextColor(100,116,139);
      pdfText(doc, title, W - 40, 72, { align: "right" });
      doc.text(new Date(a.created_at).toLocaleString(), W - 40, 86, { align: "right" });
      if (bc) { try { doc.addImage(bc, "PNG", W - 220, 100, 180, 42); doc.setFontSize(7); doc.setTextColor(120,120,120); doc.text(a.codigo, W - 130, 152, { align: "center" }); } catch(e){ console.warn("Albaran barcode embed failed:", e); } }

      let y = 130;
      doc.setDrawColor(226,232,240); doc.line(40, y, W - 40, y); y += 22;
      doc.setFont("helvetica","bold"); doc.setFontSize(13); doc.setTextColor(15,23,42); doc.text(a.codigo, 40, y); y += 20;
      doc.setFont("helvetica","normal"); doc.setFontSize(10); doc.setTextColor(60,60,60);
      pdfText(doc, `${S.tracking}: ${a.tracking_code || "—"}   ${a.bultos || 1} ${S.packages.toLowerCase()} · ${a.peso || 0} kg`, 40, y); y += 14;
      if (a.direccion) {
        const addrStr = `${S.address}: ${a.direccion}, ${a.ciudad || ""} ${a.pais || ""}`;
        const lines = doc.splitTextToSize(addrStr, W - 80);
        lines.forEach(line => { pdfText(doc, line, 40, y); y += 12; });
      }

      y += 20;
      doc.setDrawColor(0); doc.line(40, y, W - 40, y); y += 20;
      doc.setFont("helvetica","bold"); doc.setFontSize(10);
      pdfText(doc, S.received_by.toUpperCase(), 40, y); y += 16;
      doc.setFont("helvetica","normal"); doc.setFontSize(10);
      pdfText(doc, `${S.receiver}: ${a.receptor_nombre || "—"}`, 40, y); y += 12;
      pdfText(doc, `${S.id_document}: ${a.receptor_dni || "—"}`, 40, y); y += 12;

      if (a.incidencia) {
        y += 10; doc.setFont("helvetica","bold"); doc.setTextColor(185,28,28); doc.setFontSize(9);
        pdfText(doc, S.notes.toUpperCase() + ":", 40, y); y += 12;
        doc.setFont("helvetica","normal"); doc.setTextColor(30,30,30);
        const l = doc.splitTextToSize(a.incidencia, W - 80);
        l.forEach(line => { pdfText(doc, line, 40, y); y += 12; });
      }

      y += 20;
      doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(15,23,42);
      pdfText(doc, S.signature.toUpperCase() + ":", 40, y); y += 8;
      if (a.firma_data) {
        try { doc.addImage(a.firma_data, "PNG", 40, y, 200, 80); } catch(e){ console.warn("Albaran signature embed failed:", e); }
      } else {
        doc.setDrawColor(180); doc.line(40, y + 60, 240, y + 60);
      }
      y += 90;
      // ============ Photo evidence pages ============
      if (photoBlobs.length > 0) {
        doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(15,23,42);
        pdfText(doc, (S.photo_evidence || "EVIDENCIA FOTOGRAFICA") + ":", 40, y); y += 6;
        doc.setDrawColor(226,232,240); doc.line(40, y, W - 40, y); y += 10;
        // 2-per-row grid, 240x170 pt cells (with 20pt gutter)
        const cellW = (W - 80 - 20) / 2;
        const cellH = 170;
        let col = 0;
        for (const p of photoBlobs) {
          if (y + cellH > H - 60) {
            doc.addPage(); y = 40; col = 0;
            doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(15,23,42);
            pdfText(doc, (S.photo_evidence || "EVIDENCIA FOTOGRAFICA") + " (cont.):", 40, y); y += 16;
          }
          const x = 40 + col * (cellW + 20);
          try {
            doc.addImage(p.dataUrl, p.fmt, x, y, cellW, cellH - 14, undefined, "FAST");
          } catch (e) { console.warn("Photo embed failed:", e); }
          doc.setFontSize(7); doc.setTextColor(120,120,120);
          doc.text((p.name || "").slice(0, 40), x, y + cellH - 2);
          col += 1;
          if (col >= 2) { col = 0; y += cellH; }
        }
        if (col === 1) y += cellH;
        y += 12;
      }
      doc.setFontSize(7); doc.setTextColor(150,150,150);
      doc.text(`Meos Cargo · ${a.codigo} · ${new Date().toLocaleString()}`, W/2, 810, { align: "center" });
      doc.save(`${a.codigo}.pdf`);
      toast.success("PDF");
    }
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";

  return (
    <div className="space-y-4" data-testid="albaranes-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><FileText className="w-5 h-5 text-red-600" /> {t("delivery_receipts") || "Albaranes"}</h2>
          <p className="text-sm text-slate-500">{rows.length}</p>
        </div>
        <button data-testid="new-albaran-btn" onClick={()=>{setForm(EMPTY); setShowForm(true);}} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> {t("new") || "Nuevo"}</button>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400"><FileText className="w-14 h-14 mx-auto opacity-30 mb-3" /><p>{t("no_delivery_notes")}</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map(a => (
            <div key={a.id} data-testid={`alb-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-mono font-semibold text-sm">{a.codigo}</div>
                  <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${a.tipo === "recogida" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{t(a.tipo)}</span>
                </div>
                <div className="flex gap-1">
                  <PdfLangMenu testid={`alb-print-${a.id}`} compact icon={<Printer className="w-4 h-4" />} onPrint={(lg)=>printPDF(a, lg)} />
                  <button onClick={()=>remove(a.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-xs text-slate-600 space-y-0.5">
                <div><b>{t("tracking")}:</b> {a.tracking_code || "—"}</div>
                <div><b>{t("receiver")}:</b> {a.receptor_nombre || "—"}</div>
                <div>{a.bultos || 1} {t("packages_count").toLowerCase()} · {a.peso || 0} kg · <span className="text-[10px] font-semibold uppercase">{a.estado}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg">{t("new_delivery_note")}</h3>
              <button type="button" onClick={()=>setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={()=>setForm({...form, tipo: "recogida"})} data-testid="af-tipo-recogida" className={`py-2.5 rounded-lg border-2 font-semibold text-sm ${form.tipo === "recogida" ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`}>{t("pickup")}</button>
              <button type="button" onClick={()=>setForm({...form, tipo: "entrega"})} data-testid="af-tipo-entrega" className={`py-2.5 rounded-lg border-2 font-semibold text-sm ${form.tipo === "entrega" ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`}>{t("delivery")}</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("shipment")}</label>
                <select value={form.envio_id} onChange={(e)=>{const s=shipments.find(x=>x.id===e.target.value); setForm({...form, envio_id: e.target.value, tracking_code: s?.tracking_code || "", cliente_id: s?.client_id || ""});}} className={inp}>
                  <option value="">—</option>
                  {shipments.map(s => <option key={s.id} value={s.id}>{s.tracking_code} · {s.origen} → {s.destino}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("client")}</label>
                <select value={form.cliente_id} onChange={(e)=>setForm({...form, cliente_id: e.target.value})} className={inp}>
                  <option value="">—</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.fullname} {c.empresa ? `(${c.empresa})` : ""}</option>)}
                </select>
              </div>
              <div className="col-span-2"><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("address")}</label><input value={form.direccion} onChange={(e)=>setForm({...form, direccion: e.target.value})} className={inp} /></div>
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("city")}</label><input value={form.ciudad} onChange={(e)=>setForm({...form, ciudad: e.target.value})} className={inp} /></div>
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("country")}</label><input value={form.pais} onChange={(e)=>setForm({...form, pais: e.target.value})} className={inp} /></div>
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("packages_count")}</label><input type="number" min="1" value={form.bultos} onChange={(e)=>setForm({...form, bultos: +e.target.value})} className={inp} /></div>
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("package_weight_kg")}</label><input type="number" step="0.1" value={form.peso} onChange={(e)=>setForm({...form, peso: +e.target.value})} className={inp} /></div>
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("receiver_name")}</label><input data-testid="af-receptor" value={form.receptor_nombre} onChange={(e)=>setForm({...form, receptor_nombre: e.target.value})} className={inp} /></div>
              <div><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">DNI</label><input value={form.receptor_dni} onChange={(e)=>setForm({...form, receptor_dni: e.target.value})} className={inp} /></div>
              <div className="col-span-2"><label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{t("incidence_optional")}</label><input value={form.incidencia} onChange={(e)=>setForm({...form, incidencia: e.target.value})} className={inp} /></div>
            </div>

            <SignaturePad value={form.firma_data} onChange={(v)=>setForm({...form, firma_data: v})} t={t} />

            {/* Photo evidence: ID document + merchandise + extras */}
            <PhotoUploader
              scope="albaran"
              value={form.photo_ids}
              onChange={(ids) => setForm({...form, photo_ids: ids})}
              label={`Fotos · ${form.tipo === "recogida" ? "Recogida" : "Entrega"} (ID cliente, mercancía, evidencia)`}
              testid="albaran-photos"
              max={8}
            />

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="af-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save_and_pdf")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SignaturePad({ value, onChange, t }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, []); // eslint-disable-line
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t?.clientX ?? e.clientX) - r.left, y: (t?.clientY ?? e.clientY) - r.top };
  };
  const start = (e) => { setDrawing(true); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => { setDrawing(false); onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); onChange(""); };
  return (
    <div className="border-2 border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1"><PenLine className="w-4 h-4" /> {t("signature_receiver")}</span>
        <button type="button" onClick={clear} className="text-xs text-red-600 hover:underline flex items-center gap-1"><Eraser className="w-3 h-3" /> {t("clear")}</button>
      </div>
      <canvas ref={canvasRef} data-testid="signature-pad" width={520} height={140} className="w-full bg-white border border-slate-300 rounded touch-none" onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
    </div>
  );
}
